import type {
  AcademicDate,
  Assignment,
  AssignmentStatus,
  Course,
  CourseAnnouncement,
  GPASnapshot,
  GradeProjection,
  LectureNote,
  Professor,
  ProfessorReview,
  StudyGroup,
} from "../models";
import { daysFromToday } from "../mockDate";
import { CURRENT_USER_AUTHOR, authorForId } from "./people";

/**
 * Ports of `MockAcademicService.swift` and `MockProfessorService.swift`.
 *
 * These two are kept together because the course records hold Professor objects
 * directly — `Course.professor` is a Professor, never a name string — so the
 * professor fixtures have to exist before the course list can be built.
 */

/* ---------------- Professors ---------------- */

export const WILLIAMS: Professor = {
  id: "prof-williams",
  displayName: "Dr. Sarah Williams",
  department: "Computer Science",
  email: "s.williams@utexas.edu",
  officeLocation: "GDC 5.302",
  officeHours: "Tu/Th 2-4pm",
  averageRating: 4.6,
  ratingCount: 28,
};

export const LIU: Professor = {
  id: "prof-liu",
  displayName: "Prof. James Liu",
  department: "English",
  email: "j.liu@utexas.edu",
  officeLocation: "PAR 108",
  officeHours: "M 3-5pm",
  averageRating: 4.2,
  ratingCount: 15,
};

export const VASQUEZ: Professor = {
  id: "prof-vasquez",
  displayName: "Dr. Elena Vasquez",
  department: "Mathematics",
  email: "e.vasquez@utexas.edu",
  officeLocation: "RLM 12.104",
  officeHours: "W 1-3pm",
  averageRating: 3.9,
  ratingCount: 22,
};

/**
 * Reviewers and announcement authors outside the main cast. No photos, so these
 * are the seed's real exercise of the initials-on-pastel avatar fallback.
 *
 * IDs are `rev-*` rather than the `u*` space the Swift fixtures used. The
 * originals collided: review author `u10` was "Maya Rodriguez" while cast `u10`
 * is Zara Mitchell, and `u18` was "Priya Patel" who is actually `u6`. Any view
 * that resolves an author by id would render the wrong person under the right
 * name. A separate id space makes that impossible instead of merely unlikely.
 */
function reviewer(id: string, displayName: string, isVerified: boolean) {
  return { id: `rev-${id}`, displayName, school: "UT Austin", isVerified };
}

const delay = (seconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, seconds * 1000));

export class MockProfessorService {
  private professors = new Map<string, Professor>([
    ["prof-williams", { ...WILLIAMS }],
    ["prof-liu", { ...LIU }],
    ["prof-vasquez", { ...VASQUEZ }],
  ]);

  private reviews = new Map<string, ProfessorReview[]>([
    [
      "prof-williams",
      [
        {
          id: "r1",
          professorId: "prof-williams",
          courseId: "cs101",
          rating: 5,
          text: "Best CS professor I've had. Makes data structures actually fun and her office hours are super helpful.",
          author: reviewer("u10", "Maya Rodriguez", true),
          createdAt: daysFromToday(-171, 10, 0),
        },
        {
          id: "r2",
          professorId: "prof-williams",
          courseId: "cs101",
          rating: 4,
          text: "Tough grader but fair. The assignments are challenging and you learn a lot.",
          author: reviewer("u11", "Chris Park", true),
          createdAt: daysFromToday(-197, 14, 30),
        },
        {
          id: "r3",
          professorId: "prof-williams",
          courseId: "cs101",
          rating: 5,
          text: "Clear lectures, great examples, genuinely cares about students.",
          author: reviewer("u12", "Aisha Johnson", false),
          createdAt: daysFromToday(-238, 9, 15),
        },
        {
          id: "r4",
          professorId: "prof-williams",
          courseId: "cs101",
          rating: 4,
          text: "Sometimes moves fast but the slides are thorough.",
          author: reviewer("u13", "Diego Morales", true),
          createdAt: daysFromToday(-273, 16, 0),
        },
      ],
    ],
    [
      "prof-liu",
      [
        {
          id: "r5",
          professorId: "prof-liu",
          courseId: "eng102",
          rating: 5,
          text: "Prof. Liu makes writing enjoyable. Thoughtful feedback on every essay.",
          author: reviewer("u14", "Sophia Chen", true),
          createdAt: daysFromToday(-158, 11, 0),
        },
        {
          id: "r6",
          professorId: "prof-liu",
          courseId: "eng102",
          rating: 4,
          text: "Great discussions in class. Be prepared to participate a lot.",
          author: reviewer("u15", "Brandon Wells", false),
          createdAt: daysFromToday(-202, 8, 45),
        },
        {
          id: "r7",
          professorId: "prof-liu",
          courseId: "eng102",
          rating: 3,
          text: "Good professor but the workload is heavy for a writing class.",
          author: reviewer("u16", "Taylor Kim", true),
          createdAt: daysFromToday(-228, 13, 0),
        },
      ],
    ],
    [
      "prof-vasquez",
      [
        {
          id: "r8",
          professorId: "prof-vasquez",
          courseId: "math1b",
          rating: 4,
          text: "Solid lecturer. Explains integration techniques step by step.",
          author: reviewer("u17", "Jordan Lee", true),
          createdAt: daysFromToday(-157, 10, 30),
        },
        {
          id: "r9",
          professorId: "prof-vasquez",
          courseId: "math1b",
          rating: 3,
          text: "Office hours are helpful but her exams are really tough.",
          author: reviewer("u18", "Priya Nair", true),
          createdAt: daysFromToday(-176, 15, 0),
        },
        {
          id: "r10",
          professorId: "prof-vasquez",
          courseId: "math1b",
          rating: 5,
          text: "Underrated professor. If you put in the work you'll do great.",
          author: reviewer("u19", "Marcus Brown", false),
          createdAt: daysFromToday(-212, 9, 0),
        },
        {
          id: "r11",
          professorId: "prof-vasquez",
          courseId: "math1b",
          rating: 3,
          text: "Moves quickly through material. Go to office hours.",
          author: reviewer("u20", "Emma Davis", true),
          createdAt: daysFromToday(-248, 14, 0),
        },
        {
          id: "r12",
          professorId: "prof-vasquez",
          courseId: "math1b",
          rating: 4,
          text: "Fair tests, good at answering questions during lecture.",
          author: reviewer("u21", "Noah Garcia", true),
          createdAt: daysFromToday(-263, 11, 0),
        },
      ],
    ],
  ]);

  async getProfessor(professorId: string): Promise<Professor> {
    await delay(0.3);
    const professor = this.professors.get(professorId);
    if (!professor) throw new Error(`Unknown professor: ${professorId}`);
    return professor;
  }

  async getReviews(professorId: string): Promise<ProfessorReview[]> {
    await delay(0.3);
    return this.reviews.get(professorId) ?? [];
  }

  async submitReview(
    professorId: string,
    courseId: string,
    rating: number,
    text?: string,
  ): Promise<ProfessorReview> {
    await delay(0.5);
    const review: ProfessorReview = {
      id: `r-${crypto.randomUUID()}`,
      professorId,
      courseId,
      rating,
      text,
      author: CURRENT_USER_AUTHOR,
      createdAt: new Date().toISOString(),
    };

    const all = [review, ...(this.reviews.get(professorId) ?? [])];
    this.reviews.set(professorId, all);

    // Recalculate the average so the header updates behind the new review.
    const professor = this.professors.get(professorId);
    if (professor) {
      const total = all.reduce((sum, r) => sum + r.rating, 0);
      professor.averageRating = total / all.length;
      professor.ratingCount = all.length;
    }

    return review;
  }
}

/* ---------------- Academic ---------------- */

export class MockAcademicService {
  private courses: Course[] = [
    {
      id: "cs101",
      name: "Intro to Computer Science",
      code: "CS 101",
      professor: WILLIAMS,
      schedule: "MWF 10:00-10:50",
      location: "GDC 2.216",
      credits: 3,
      currentGrade: 92.5,
      officeHours: "Tu/Th 2-4pm",
    },
    {
      id: "eng102",
      name: "Rhetoric and Writing",
      code: "ENG 102",
      professor: LIU,
      schedule: "TTh 1:00-2:15",
      location: "PAR 301",
      credits: 3,
      currentGrade: 88.0,
      officeHours: "M 3-5pm",
    },
    {
      id: "math1b",
      name: "Calculus II",
      code: "MATH 1B",
      professor: VASQUEZ,
      schedule: "MWF 11:00-11:50",
      location: "RLM 5.104",
      credits: 4,
      currentGrade: 78.5,
      officeHours: "W 1-3pm",
    },
  ];

  private announcements = new Map<string, CourseAnnouncement[]>([
    [
      "cs101",
      [
        {
          id: "ann1",
          courseId: "cs101",
          title: "Midterm moved back one week",
          body: "Due to scheduling conflicts, the midterm has been rescheduled. Same room, same time slot.",
          emoji: "📅",
          author: reviewer("u22", "Sarah Kim", true),
          isPinned: true,
          postedAt: daysFromToday(0, 9, 30),
        },
        {
          id: "ann2",
          courseId: "cs101",
          title: "Friday lecture moved to GDC 1.304",
          body: "This Friday's lecture will be in GDC 1.304 instead of the usual room. Dr. Williams said it's a one-time change.",
          emoji: "🚪",
          author: authorForId("u3"),
          isPinned: false,
          postedAt: daysFromToday(1, 14, 15),
        },
      ],
    ],
    [
      "eng102",
      [
        {
          id: "ann3",
          courseId: "eng102",
          title: "Research paper deadline extended",
          body: "Prof. Liu extended the research paper draft deadline by four days.",
          emoji: "⏰",
          author: authorForId("u2"),
          isPinned: true,
          postedAt: daysFromToday(2, 11, 0),
        },
      ],
    ],
    [
      "math1b",
      [
        {
          id: "ann4",
          courseId: "math1b",
          title: "No office hours this Wednesday",
          body: "Dr. Vasquez canceled Wednesday office hours this week. She said to email questions instead.",
          emoji: "🚫",
          author: authorForId("u1"),
          isPinned: true,
          postedAt: daysFromToday(-1, 16, 45),
        },
        {
          id: "ann5",
          courseId: "math1b",
          title: "Review session added Saturday 2pm",
          body: "TA is holding an extra review session Saturday at 2pm in RLM 5.104 before the midterm. Bring your problem sets.",
          emoji: "📚",
          author: reviewer("u23", "Chris Nguyen", false),
          isPinned: false,
          postedAt: daysFromToday(2, 10, 20),
        },
      ],
    ],
  ]);

  async getCourses(): Promise<Course[]> {
    await delay(0.3);
    return this.courses;
  }

  async addCourse(
    name: string,
    code?: string,
    professor?: Professor,
    schedule?: string,
  ): Promise<Course> {
    await delay(0.3);
    const course: Course = { id: `course-${crypto.randomUUID()}`, name, code, professor, schedule };
    this.courses.push(course);
    return course;
  }

  async removeCourse(courseId: string): Promise<void> {
    this.courses = this.courses.filter((c) => c.id !== courseId);
  }

  async getAssignments(courseId?: string): Promise<Assignment[]> {
    await delay(0.3);
    const all: Assignment[] = [
      {
        id: "a1",
        courseId: "cs101",
        title: "Programming Assignment 3",
        dueDate: daysFromToday(2, 23, 59),
        status: "upcoming",
        weight: 0.1,
        description: "Implement a linked list",
      },
      {
        id: "a2",
        courseId: "eng102",
        title: "Research Paper Draft",
        dueDate: daysFromToday(5, 23, 59),
        status: "inProgress",
        weight: 0.2,
      },
      {
        id: "a3",
        courseId: "math1b",
        title: "Problem Set 5",
        dueDate: daysFromToday(1, 23, 59),
        status: "upcoming",
        weight: 0.05,
      },
      {
        id: "a4",
        courseId: "cs101",
        title: "Programming Assignment 2",
        dueDate: daysFromToday(-5, 23, 59),
        status: "graded",
        grade: 95.0,
        weight: 0.1,
      },
      {
        id: "a5",
        courseId: "math1b",
        title: "Midterm Exam",
        dueDate: daysFromToday(10, 10, 0),
        status: "upcoming",
        weight: 0.25,
      },
    ];
    return all
      .filter((a) => courseId === undefined || a.courseId === courseId)
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  }

  async updateAssignmentStatus(_id: string, _status: AssignmentStatus): Promise<void> {
    await delay(0.2);
  }

  async getLectureNotes(courseId: string): Promise<LectureNote[]> {
    await delay(0.3);
    return [
      {
        id: "ln1",
        courseId,
        title: "Lecture 12: Data Structures",
        content: "Today we covered binary trees and their traversal methods...",
        conceptTags: ["binary trees", "traversal", "BFS", "DFS"],
        author: authorForId("u1"),
        isBestNote: true,
        recordedAt: daysFromToday(-2, 10, 0),
      },
      {
        id: "ln2",
        courseId,
        title: "Lecture 11: Recursion",
        content: "Recursion is when a function calls itself...",
        conceptTags: ["recursion", "base case", "stack overflow"],
        author: authorForId("u3"),
        isBestNote: false,
        recordedAt: daysFromToday(-4, 10, 0),
      },
    ];
  }

  async uploadLectureNote(courseId: string, title: string, content: string): Promise<LectureNote> {
    await delay(0.5);
    return {
      id: `ln-${crypto.randomUUID()}`,
      courseId,
      title,
      content,
      conceptTags: [],
      author: CURRENT_USER_AUTHOR,
      isBestNote: false,
      recordedAt: daysFromToday(0, 12, 0),
    };
  }

  async getStudyGroups(courseId?: string): Promise<StudyGroup[]> {
    await delay(0.3);
    const all: StudyGroup[] = [
      {
        id: "sg1",
        courseId: "cs101",
        name: "CS 101 Study Squad",
        memberCount: 5,
        nextSession: daysFromToday(1, 18, 0),
        sharedConfusionAreas: ["recursion", "pointers"],
        groupChatId: "gc-sg1",
        photoURL: "https://picsum.photos/seed/studysquad/200/200",
      },
      {
        id: "sg2",
        courseId: "math1b",
        name: "Calc Crew",
        memberCount: 3,
        nextSession: daysFromToday(2, 14, 0),
        sharedConfusionAreas: ["integration by parts"],
        groupChatId: "gc-sg2",
        photoURL: "https://picsum.photos/seed/calccrew/200/200",
      },
    ];
    return courseId === undefined ? all : all.filter((g) => g.courseId === courseId);
  }

  async createStudyGroup(courseId: string, name: string): Promise<StudyGroup> {
    await delay(0.5);
    return {
      id: `sg-${crypto.randomUUID()}`,
      courseId,
      name,
      memberCount: 1,
      sharedConfusionAreas: [],
    };
  }

  async joinStudyGroup(_groupId: string): Promise<void> {
    await delay(0.3);
  }

  async getGradeProjection(courseId?: string): Promise<GradeProjection> {
    await delay(0.3);
    const overall = courseId === undefined;
    return {
      courseId,
      currentGrade: overall ? 86.3 : 92.5,
      projectedGrade: overall ? 87.0 : 91.0,
      scenarios: [
        { description: "If you ace the final", projectedGrade: 95.0 },
        { description: "If you maintain current pace", projectedGrade: overall ? 87.0 : 91.0 },
        { description: "If scores drop 10%", projectedGrade: overall ? 80.0 : 85.0 },
      ],
    };
  }

  async getOverallGPA(): Promise<number> {
    await delay(0.2);
    return 3.54;
  }

  async getGPAHistory(): Promise<GPASnapshot[]> {
    await delay(0.2);
    const gpas = [3.41, 3.45, 3.45, 3.48, 3.5, 3.52, 3.54];
    return gpas.map((gpa, i) => ({
      id: `d${6 - i}`,
      date: daysFromToday(-(6 - i), 0, 0),
      gpa,
    }));
  }

  async getCourseAnnouncements(courseId: string): Promise<CourseAnnouncement[]> {
    await delay(0.3);
    return [...(this.announcements.get(courseId) ?? [])].sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      return b.postedAt.localeCompare(a.postedAt);
    });
  }

  async postCourseAnnouncement(
    courseId: string,
    title: string,
    body: string,
    emoji?: string,
  ): Promise<CourseAnnouncement> {
    await delay(0.5);
    const announcement: CourseAnnouncement = {
      id: `ann-${crypto.randomUUID()}`,
      courseId,
      title,
      body,
      emoji,
      author: CURRENT_USER_AUTHOR,
      isPinned: false,
      postedAt: new Date().toISOString(),
    };
    this.announcements.set(courseId, [...(this.announcements.get(courseId) ?? []), announcement]);
    return announcement;
  }

  async getAcademicDates(): Promise<AcademicDate[]> {
    await delay(0.3);
    return [
      {
        id: "ad1",
        title: "Dr. Williams Office Hours",
        date: daysFromToday(2, 14, 0),
        category: "officeHours",
        courseId: "cs101",
        notes: "GDC 5.302",
      },
      {
        id: "ad2",
        title: "Prof. Liu Office Hours",
        date: daysFromToday(4, 15, 0),
        category: "officeHours",
        courseId: "eng102",
        notes: "PAR 108",
      },
      {
        id: "ad3",
        title: "Last Day to Drop w/o W",
        date: daysFromToday(8, 23, 59),
        category: "dropDeadline",
      },
      {
        id: "ad4",
        title: "Fall Break",
        date: daysFromToday(11, 0, 0),
        category: "holiday",
        notes: "Five days",
      },
      {
        id: "ad5",
        title: "Midterm Exam Period Begins",
        date: daysFromToday(5, 0, 0),
        category: "examPeriod",
      },
      {
        id: "ad6",
        title: "Spring Registration Opens",
        date: daysFromToday(15, 8, 0),
        category: "registrationOpen",
      },
      {
        id: "ad7",
        title: "Dr. Vasquez Office Hours",
        date: daysFromToday(6, 13, 0),
        category: "officeHours",
        courseId: "math1b",
        notes: "RLM 12.104",
      },
    ];
  }
}
